// A delay that keeps ticking while the tab is in the background.
//
// Browsers throttle a hidden tab's main-thread timers (~1/s, then ~1/min after
// five minutes), which stalls the Firefly / Photoshop generation poll loops
// when the user minimizes the window or switches tabs — the server-side job
// finishes, but the client stops picking up the result. A dedicated worker's
// timers are not throttled that way, and `fetch` and `message` events still run
// on a hidden page, so driving each loop's delay from a worker keeps generation
// progressing regardless of tab visibility.
//
// Degrades gracefully to a plain (throttleable) `setTimeout` when workers are
// unavailable, so callers never have to branch on support.

// `null` = not yet created, `false` = unavailable (construction failed), else
// the live Worker.
let worker = null;
let nextId = 1;
const pending = new Map();

const abortError = () => {
  try {
    return new DOMException("The operation was aborted.", "AbortError");
  } catch {
    const error = new Error("The operation was aborted.");
    error.name = "AbortError";
    return error;
  }
};

const getWorker = () => {
  if (worker !== null) {
    return worker;
  }

  try {
    worker = new Worker(new URL("./timerWorker.js", import.meta.url), {
      type: "module",
    });

    worker.onmessage = (event) => {
      const resolve = pending.get(event.data?.id);

      if (resolve) {
        pending.delete(event.data.id);
        resolve();
      }
    };

    worker.onerror = () => {
      // A broken worker must not wedge every pending delay: resolve them so the
      // callers fall through to their next poll rather than hanging forever, and
      // stop trying to reconstruct it on every call.
      pending.forEach((resolve) => resolve());
      pending.clear();
      worker = false;
    };
  } catch {
    worker = false;
  }

  return worker;
};

/**
 * Resolves after `ms`, driven by an off-main-thread timer so it survives
 * background-tab throttling. Rejects with an `AbortError` if `signal` fires
 * first, matching how `fetch`/axios abort the surrounding request.
 *
 * @param {number} ms - Delay in milliseconds.
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<void>}
 */
export const backgroundDelay = (ms, { signal } = {}) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    const activeWorker = getWorker();

    // No worker available — degrade to a regular (throttleable) timer, but keep
    // the same abort semantics.
    if (!activeWorker) {
      const onAbort = () => {
        clearTimeout(handle);
        reject(abortError());
      };
      const handle = setTimeout(() => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }, ms);
      signal?.addEventListener("abort", onAbort, { once: true });
      return;
    }

    const id = nextId++;

    const onAbort = () => {
      pending.delete(id);
      activeWorker.postMessage({ type: "clear", id });
      reject(abortError());
    };

    pending.set(id, () => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    });

    signal?.addEventListener("abort", onAbort, { once: true });
    activeWorker.postMessage({ type: "start", id, delay: ms });
  });
